import { upperFirst, isString, isNil } from "lodash-es";
import moment from "moment";
import ICatalog_Item from "../models/ICatalog_Item";
import ICatalog_Item_Vendor_Source_Map from "../models/ICatalog_Item_Vendor_Source_Map";
import IVendor_Source from "../models/IVendor_Source";
import ISupplierModel from "../models/ISupplierModel";
import IPrice_Group from "../models/IPrice_Group";
import IOrder_Item from "../models/IOrder_Item";
import ICart_Item from "../models/ICart_Item";
import IKit_Builder_Data from "../models/IKit_Builder_Data";
import IKit_Builder_Option_Item from "../models/IKit_Builder_Option_Item";
import IKit_Builder_Option from "../models/IKit_Builder_Option";
import IPrice_Band_Compiled from "../models/IPrice_Band_Compiled";
import IPrice_Band from "../models/IPrice_Band";
import INewOrder_Item_Option from "../models/INewOrder_Item_Option";
import IWarehouse from "@models/IWarehouse";
import IUserUI from "../models/IUserUI";
import IVendorModel from "../models/IVendorModel";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IOnTypeaheadCatalogItemDTO from "../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemRestDTO from "../models/DTO/ITypeaheadCatalogItemRestDTO";
import ILookupCatalogItemBySkuRestDTO from "../models/DTO/ILookupCatalogItemBySkuRestDTO";
import IOnLookupCatalogItemBySkuDTO from "../models/DTO/IOnLookupCatalogItemBySkuDTO";

export const getVendorSourceExpectedDate = (vendorSource: IVendor_Source, item: ICatalog_Item): string => {
    if (vendorSource) {
        let leadTime = vendorSource.default_lead_time;
        let leadTimeType = vendorSource.default_lead_time_type;

        let itemMap: ICatalog_Item_Vendor_Source_Map | undefined;

        if (item?.Catalog_Item_Vendor_Source_Maps) {
            for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
                if (mapItem.vendor_source_id === vendorSource.id) {
                    itemMap = mapItem;
                    break;
                }
            }
        }

        if (itemMap) {
            if (itemMap.lead_time && itemMap.lead_time_type) {
                leadTime = itemMap.lead_time;
                leadTimeType = itemMap.lead_time_type;
            }
        }

        const leadTimeEstimatedDate = moment().add(leadTime, leadTimeType).format("YYYY-MM-DD");

        return leadTimeEstimatedDate;
    } else {
        return "";
    }
};

export const getLeadTime = (vendorSource: IVendor_Source, item: ICatalog_Item): string => {
    let leadTime = vendorSource.default_lead_time;
    let leadTimeType = vendorSource.default_lead_time_type;

    let itemMap: ICatalog_Item_Vendor_Source_Map | undefined;

    if (item.Catalog_Item_Vendor_Source_Maps) {
        for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
            if (mapItem.vendor_source_id === vendorSource.id) {
                itemMap = mapItem;
                break;
            }
        }
    }

    if (itemMap) {
        if (itemMap.lead_time && itemMap.lead_time_type) {
            leadTime = itemMap.lead_time;
            leadTimeType = itemMap.lead_time_type;
        }
    }

    return leadTime + " " + upperFirst(leadTimeType);
};

export const getVendorSourceMinOrderQty = (vendorSource: IVendor_Source, item: ICatalog_Item): number => {
    let minQty = 1;

    if (item?.Catalog_Item_Vendor_Source_Maps && vendorSource) {
        for (const mapItem of item.Catalog_Item_Vendor_Source_Maps) {
            if (mapItem.vendor_source_id === vendorSource.id) {
                if (mapItem.min_order_qty) {
                    minQty = mapItem.min_order_qty;
                }

                break;
            }
        }
    }

    return Number(minQty);
};

/**
 * If the catalog item has a pack qty, but the pricing is not priced per pack, but per item, get the order qty.
 *
 * @param catalogItem
 * @param qtyInput
 */
export const getPackItemOrderQty = (catalogItem: ICatalog_Item, qtyInput: number) => {
    let qty = qtyInput;

    if (catalogItem.has_pack_qty) {
        if (catalogItem.pack_qty && catalogItem.pack_qty >= 1 && !catalogItem.price_per_pack) {
            qty = catalogItem.pack_qty * qtyInput;
        }
    }

    return qty;
};

/**
 * Given a stored unit qty, return the number of whole packs it represents.
 *
 * When the item is not priced per pack, the stored qty is in units, so divide by pack_qty
 * and snap to the nearest whole pack. When priced per pack, the stored qty already is the
 * pack count, so return it unchanged.
 *
 * @param catalogItem
 * @param storedQty
 */
export const getPackItemPackCount = (catalogItem: ICatalog_Item | undefined, storedQty: number) => {
    if (catalogItem?.has_pack_qty && catalogItem.pack_qty && catalogItem.pack_qty >= 1) {
        return catalogItem.price_per_pack ? storedQty : Math.round(storedQty / catalogItem.pack_qty);
    }

    return storedQty;
};

/**
 * Given a stored qty, return the total number of units it represents.
 *
 * When priced per pack, the stored qty is a pack count, so multiply by pack_qty. When not
 * priced per pack, the stored qty already is in units, so return it unchanged.
 *
 * @param catalogItem
 * @param storedQty
 */
export const getPackItemTotalUnits = (catalogItem: ICatalog_Item | undefined, storedQty: number) => {
    if (catalogItem?.has_pack_qty && catalogItem.pack_qty && catalogItem.pack_qty >= 1) {
        return catalogItem.price_per_pack ? storedQty * catalogItem.pack_qty : storedQty;
    }

    return storedQty;
};

export const getItemQtyOnHandBasedOnMaterials = (catalogItem: ICatalog_Item): number => {
    let qtyOnHand = 0;

    if (catalogItem.Material_Catalog_Item_Maps?.length) {
        const maxItemByMaterials: {
            material: string;
            maxQty: number;
        }[] = [];

        for (const materialMap of catalogItem.Material_Catalog_Item_Maps) {
            const material = materialMap.Material!;
            const materialQtyInItem = materialMap.qty;
            const materialQty = material?.qty_on_hand || 0;

            const maxQty = materialQty / materialQtyInItem;

            maxItemByMaterials.push({ material: material?.name, maxQty });
        }

        let maxQtyOnHandBasedOnMaterial: number | undefined;

        for (const maxItemByMaterial of maxItemByMaterials) {
            if (isNil(maxQtyOnHandBasedOnMaterial)) {
                maxQtyOnHandBasedOnMaterial = maxItemByMaterial.maxQty;
            } else {
                if (maxQtyOnHandBasedOnMaterial && maxQtyOnHandBasedOnMaterial > maxItemByMaterial.maxQty) {
                    maxQtyOnHandBasedOnMaterial = maxItemByMaterial.maxQty;
                }
            }
        }

        if (maxQtyOnHandBasedOnMaterial) {
            qtyOnHand = maxQtyOnHandBasedOnMaterial;
        }
    }

    return qtyOnHand;
};

export const getCatalogItemDescription = (
    supplierModel: ISupplierModel,
    catalogItem: ICatalog_Item,
    priceGroupModel?: IPrice_Group,
    orderItem?: IOrder_Item
): string | undefined => {
    let description = catalogItem.description;

    if (supplierModel.has_pricing_descriptions) {
        if (priceGroupModel) {
            const pricingDescriptions = catalogItem.pricing_descriptions;

            if (
                pricingDescriptions &&
                isString(pricingDescriptions[priceGroupModel.value]) &&
                pricingDescriptions[priceGroupModel.value].length
            ) {
                description = pricingDescriptions[priceGroupModel.value];
            }
        }
    }

    if (orderItem) {
        if (orderItem.description) {
            description = orderItem.description;
        }
    }

    return description;
};

/**
 * Determine if the kit builder selections are complete for the given catalog item and selections.
 *
 * @param catalogItem
 * @param selections
 */
export const isKitBuilderSelectionsComplete = (
    catalogItem: ICatalog_Item,
    selections: IKit_Builder_Data
): { isComplete: boolean; issues: string[] } => {
    let isComplete = true;
    const issues: string[] = [];

    if (catalogItem.Kit_Builder_Options) {
        for (const option of catalogItem.Kit_Builder_Options) {
            const minRequired = option.min_required || 0;
            const selectionsLength = (selections[option.name]?.selections || []).length || 0;

            if (minRequired > selectionsLength) {
                isComplete = false;
                issues.push(`${option.name} requires ${minRequired} selections, found ${selectionsLength}`);
            }

            if (option.Kit_Builder_Option_Items && selections[option.name]) {
                for (const optionItem of option.Kit_Builder_Option_Items) {
                    if (optionItem.require_sub_option) {
                        // locate the option item in the selections and see if it has any sub options.
                        const selection = selections[option.name].selections.find((_selection) => {
                            return _selection.optionItem.id === optionItem.id;
                        });

                        if (selection && !(selection.subOptionSelections || []).length) {
                            isComplete = false;
                            issues.push(`${option.name} option item ${optionItem.Catalog_Item?.name} requires a sub option selection`);
                        }
                    }
                }
            }
        }
    }

    return { isComplete, issues };
};

/**
 * True when every required, enabled item option on the catalog item has a non-blank value in the
 * selections. OrderItemOption only sets the HTML `required` flag on its inputs, which cannot fire
 * inside a ReactModal portal (no enclosing form) and is not validated by the API, so callers such
 * as the kit builder modal gate their add button with this instead.
 *
 * A required checkbox counts as answered once it has been toggled (it emits "true" or "false");
 * "required" means it must be answered, not that it must be checked.
 *
 * @param catalogItem
 * @param itemOptions
 */
export const areRequiredItemOptionsSelected = (
    catalogItem: ICatalog_Item,
    itemOptions: Partial<INewOrder_Item_Option>[] | undefined
): { isComplete: boolean; missing: string[] } => {
    const missing: string[] = [];

    for (const option of catalogItem.Options || []) {
        if (option.is_enabled === false || !option.Item_Option?.required) {
            continue;
        }

        const selected = (itemOptions || []).find((itemOption) => itemOption.item_option_id === option.item_option_id);
        const value = selected?.value;

        if (value === undefined || value === null || String(value).trim() === "") {
            missing.push(option.Item_Option.name);
        }
    }

    return { isComplete: missing.length === 0, missing };
};

/**
 * True if any kit-builder option item exposes sub-options. Used to gate the in-place "edit kit"
 * flow: an existing kit line does not persist which sub-option was chosen (Kit_Items carry no
 * sub-option id), so re-opening the modal would silently lose that selection.
 */
export const kitBuilderHasSubOptions = (catalogItem?: ICatalog_Item): boolean => {
    return !!catalogItem?.Kit_Builder_Options?.some((option) =>
        (option.Kit_Builder_Option_Items || []).some((item) => (item.Sub_Kit_Builder_Option_Items || []).length > 0)
    );
};

/**
 * Reconstruct kit-builder selection data from an existing kit order item so the KitBuilderModal can
 * be re-opened pre-populated. Recovers per-line qty and length (`depth`). Sub-option selections are
 * NOT recoverable from Kit_Items -- only call this for kits where kitBuilderHasSubOptions is false.
 *
 * An order line's Catalog_Item does not carry Kit_Builder_Options, so pass the hydrated options
 * (e.g. from a catalog typeahead lookup of the kit) via `kitBuilderOptions`; otherwise it falls back
 * to whatever is on the order item.
 */
export const buildKitBuilderDataFromOrderItem = (orderItem: IOrder_Item, kitBuilderOptions?: IKit_Builder_Option[]): IKit_Builder_Data => {
    const options = kitBuilderOptions || orderItem.Catalog_Item?.Kit_Builder_Options || [];

    return buildKitBuilderDataFromChildren(
        (orderItem.Kit_Items || []).map((kitItem) => ({
            kit_option_id: kitItem.kit_option_id,
            kit_option_item_id: kitItem.kit_option_item_id,
            qty: kitItem.qty,
            length: !isNil(kitItem.depth) ? kitItem.depth : undefined,
            Catalog_Item: kitItem.Catalog_Item,
        })),
        options,
        Number(orderItem.qty)
    );
};

/**
 * Cart counterpart of buildKitBuilderDataFromOrderItem. Cart children come back flat on the cart,
 * linked to the kit line by main_kit_cart_item_id. Cart lines carry no length, so length-priced
 * components are not recoverable here.
 */
export const buildKitBuilderDataFromCartItems = (
    kitCartItem: ICart_Item,
    cartItems: ICart_Item[],
    kitBuilderOptions: IKit_Builder_Option[]
): IKit_Builder_Data => {
    return buildKitBuilderDataFromChildren(
        (cartItems || [])
            .filter((cartItem) => cartItem.main_kit_cart_item_id === kitCartItem.id)
            .map((cartItem) => ({
                kit_option_id: cartItem.kit_option_id,
                kit_option_item_id: cartItem.kit_option_item_id,
                qty: cartItem.qty,
                Catalog_Item: cartItem.Catalog_Item,
            })),
        kitBuilderOptions || [],
        Number(kitCartItem.qty)
    );
};

type KitChildLine = {
    kit_option_id?: number | null;
    kit_option_item_id?: number | null;
    qty?: number;
    length?: number;
    Catalog_Item?: ICatalog_Item;
};

/**
 * Shared core: match each child line back to its configured option / option item and recover the
 * per-kit selection qty. Children without a kit_option_id (base items, sub-selections, or lines
 * written before the ids were persisted) are skipped.
 */
const buildKitBuilderDataFromChildren = (
    children: KitChildLine[],
    options: IKit_Builder_Option[],
    parentQty: number
): IKit_Builder_Data => {
    const data: IKit_Builder_Data = {};
    // Children are stored at (kit qty x selection qty); the modal works in per-kit selection qty.
    const kitQty = parentQty > 0 ? parentQty : 1;

    for (const option of options) {
        const optionItems = option.Kit_Builder_Option_Items || [];

        const selections = children
            .filter((child) => !!child.kit_option_id && child.kit_option_id === option.id)
            .map((child) => {
                // Match back to the configured option item so OwSelectOption can resolve it by id.
                const optionItem = (optionItems.find((oi) => oi.id === child.kit_option_item_id) || {
                    id: child.kit_option_item_id,
                    Catalog_Item: child.Catalog_Item,
                }) as IKit_Builder_Option_Item;

                const selectionQty = Math.round((Number(child.qty) || kitQty) / kitQty);

                return {
                    optionItem,
                    subOptionSelections: [] as IKit_Builder_Option_Item[],
                    qty: selectionQty > 0 ? selectionQty : 1,
                    length: child.length,
                };
            });

        if (selections.length) {
            data[option.name] = { selections };
        }
    }

    return data;
};

/**
 * Check if a quantity falls within this price band's range
 *
 * @param {number} qty - The quantity to check
 * @returns {boolean} True if quantity is within band's range, false otherwise
 */
const containsQty = (qty: number, priceBand: IPrice_Band | IPrice_Band_Compiled) => {
    return qty >= priceBand.start_qty && (!priceBand.end_qty || qty <= priceBand.end_qty);
};

export const findPriceBand = (qty: number, priceBands: IPrice_Band_Compiled[]): IPrice_Band | null => {
    const match = priceBands.find((band) => {
        return containsQty(qty, band);
    });

    if (match) {
        const priceBand = match.priceBand ? match.priceBand : (match as IPrice_Band);

        return priceBand || null;
    } else {
        return null;
    }
};

const getQtyOnHandWarehouseMapItem = (catalogItem: ICatalog_Item, shippingWarehouse?: IWarehouse) => {
    return catalogItem.Warehouse_Catalog_Items_Maps?.find((map) => map.warehouse_id === shippingWarehouse?.id);
};

const typeaheadCatalogItems = async (
    catalogGuid: string,
    searchPhrase: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    options?: Omit<ITypeaheadCatalogItemRestDTO, "name">
): Promise<IOnTypeaheadCatalogItemDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.CatalogTypeaheadItems,
        params: { catalogGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: searchPhrase, ...options }),
    });

    if (!response.ok) {
        throw new Error(`Failed to typeahead catalog items: ${response.statusText}`);
    }

    return (await response.json()) as IOnTypeaheadCatalogItemDTO;
};

/**
 * Generate a SKU-like value from a catalog item name.
 *
 * Produces an upper-cased, kebab-style token (e.g. "My Cool Item" -> "MY-COOL-ITEM").
 * Diacritics are stripped and any run of non-alphanumeric characters collapses to a
 * single dash. When a maxLength is provided (e.g. 31 for QuickBooks Desktop suppliers)
 * the result is truncated without leaving a trailing dash.
 *
 * @param name - The catalog item name to derive the SKU from
 * @param maxLength - Optional maximum length for the generated SKU
 */
export const generateSkuFromName = (name: string, maxLength?: number): string => {
    if (!name) {
        return "";
    }

    let sku = name
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "") // strip diacritics
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "-") // non-alphanumeric runs -> single dash
        .replace(/^-+|-+$/g, ""); // trim leading/trailing dashes

    if (maxLength && sku.length > maxLength) {
        sku = sku.slice(0, maxLength).replace(/-+$/g, "");
    }

    return sku;
};

/**
 * Look up existing catalog items by an exact SKU within a catalog.
 *
 * Auth (userGuid / userToken / supplierGuid / vendorGuid) is sent via headers and
 * resolved server-side by AclService. Used to flag duplicate SKUs before save.
 */
const lookupCatalogItemBySku = async (
    catalogGuid: string,
    skuName: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnLookupCatalogItemBySkuDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.CatalogLookupItemBySku });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const body: ILookupCatalogItemBySkuRestDTO = { catalogGuid, skuName };

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`Failed to lookup catalog item by sku: ${response.statusText}`);
    }

    return (await response.json()) as IOnLookupCatalogItemBySkuDTO;
};

const CatalogItemService = {
    getVendorSourceExpectedDate,
    getLeadTime,
    getVendorSourceMinOrderQty,
    getPackItemOrderQty,
    getItemQtyOnHandBasedOnMaterials,
    getCatalogItemDescription,
    isKitBuilderSelectionsComplete,
    areRequiredItemOptionsSelected,
    kitBuilderHasSubOptions,
    buildKitBuilderDataFromOrderItem,
    buildKitBuilderDataFromCartItems,
    findPriceBand,
    getQtyOnHandWarehouseMapItem,
    typeaheadCatalogItems,
    generateSkuFromName,
    lookupCatalogItemBySku,
};

export default CatalogItemService;
